import type { SelectMenuOption } from 'src/components/SelectMenu';

/** Marks the Attribute field of a rule row; screens laid out around the old rows still key on it. */
export const RULE_ATTRIBUTE_FIELD_CLASS = 'rule-attribute-field';

/** An attribute as the builder's fields carry it: `label` reads "Group / Name" when grouped. */
export interface ConditionFieldOption {
  value: string | number | boolean;
  label: string;
  group?: string;
}

/** A condition group as react-querybuilder holds it: rules and nested groups. */
interface ConditionGroupLike {
  rules?: readonly unknown[];
}

const GROUP_SEPARATOR = ' / ';

function getOwnName({ label, group }: ConditionFieldOption): string {
  const prefix = `${group}${GROUP_SEPARATOR}`;
  return group && label.startsWith(prefix) ? label.slice(prefix.length) : label;
}

/**
 * The menu lists attributes under their group headings and the field shows the picked one, so
 * both read the attribute's own name. A name that more than one group carries (Label / Name
 * under both Conversation and Site) keeps its group, or the field could not tell them apart.
 */
export function getAttributeTitles(
  options: readonly ConditionFieldOption[],
): Map<string, string> {
  const ownNames = options.map(getOwnName);
  const nameCounts = new Map<string, number>();
  ownNames.forEach((name) => nameCounts.set(name, (nameCounts.get(name) ?? 0) + 1));

  return new Map(
    options.map((option, index) => {
      const ownName = ownNames[index];
      const title = (nameCounts.get(ownName) ?? 0) > 1 ? option.label : ownName;
      return [String(option.value), title];
    }),
  );
}

export function toAttributeMenuOptions(
  options: readonly ConditionFieldOption[],
): SelectMenuOption[] {
  const titles = getAttributeTitles(options);

  return [...options]
    .filter((option) => String(option.value) !== '')
    .sort((first, second) => (first.group ?? '').localeCompare(second.group ?? ''))
    .map((option) => ({
      value: String(option.value),
      title: titles.get(String(option.value)) ?? option.label,
      group: option.group || undefined,
    }));
}

/** How deep groups nest below the root: 0 with no groups, 1 for groups at the root, and so on. */
export function getConditionGroupLevel(group: ConditionGroupLike): number {
  return (group?.rules ?? [])
    .filter(isConditionGroup)
    .reduce<number>(
      (deepest, nested) => Math.max(deepest, 1 + getConditionGroupLevel(nested)),
      0,
    );
}

function isConditionGroup(rule: unknown): rule is ConditionGroupLike {
  return typeof rule === 'object' && rule !== null && 'rules' in rule;
}
